import type { CSSProperties } from "react"
import type { SignalType, VehicleCard } from "../types/api"
import { useI18n } from "../hooks/useI18n"
import checkboxOff from "../assets/figma-confirmation/checkbox-off.svg"
import checkboxOn from "../assets/figma-confirmation/checkbox-on.svg"
import figmaExact from "../generated/figma/signal-confirmation.android.no-frame.disabled.json"
import {
  exactFigmaSignalColor,
  FigmaSignalGlyph,
  hasExactFigmaSignalGlyph,
} from "./FigmaSignalGlyphLibrary"
import { FigmaStatusPlate } from "./MainFigmaVisuals"
import "./SignalConfirmationSheet.css"

type Platform = "android" | "ios"
type StatusVariant = "noFrame" | "withFrame"
type StateVariant = "disabled" | "ready"

type FigmaColor = { r: number; g: number; b: number; a?: number }
type FigmaPaint = {
  type?: string
  visible?: boolean
  opacity?: number
  color?: FigmaColor
  gradientStops?: Array<{ position: number; color: FigmaColor }>
}

type FigmaNode = {
  id?: string
  name: string
  type: string
  visible?: boolean
  opacity?: number
  x?: number
  y?: number
  width?: number
  height?: number
  cornerRadius?: number | string
  topLeftRadius?: number
  topRightRadius?: number
  bottomRightRadius?: number
  bottomLeftRadius?: number
  fills?: FigmaPaint[]
  strokes?: FigmaPaint[]
  strokeWeight?: number
  strokeAlign?: string
  characters?: string
  fontName?: { family?: string; style?: string }
  fontSize?: number
  fontWeight?: number
  lineHeight?: { unit?: string; value?: number }
  letterSpacing?: { unit?: string; value?: number }
  textAlignHorizontal?: string
}

type ExactNodes = Record<string, FigmaNode | null>

type ExactVariant = {
  platform: Platform
  status: StatusVariant
  state: StateVariant
  source: { packagePath: string; screenId: string; sheetId: string }
  screen: { width: number; height: number }
  placement: { x: number; y: number; width: number; height: number }
  nodes: ExactNodes
}

type ExactData = {
  referenceViewport: { width: number; height: number }
  variants: Record<string, ExactVariant>
}

interface SignalConfirmationSheetProps {
  platform: Platform
  vehicle: VehicleCard
  selectedSignal: SignalType
  allowReply: boolean
  confirmedReal: boolean
  isSending: boolean
  error: string
  onAllowReplyChange: (value: boolean) => void
  onConfirmedRealChange: (value: boolean) => void
  onBack: () => void
  onSend: () => void
}

const exact = figmaExact as unknown as ExactData

function variantKey(platform: Platform, status: StatusVariant, state: StateVariant) {
  return `${platform}.${status}.${state}`
}

function rgba(color?: FigmaColor, opacity = 1): string | undefined {
  if (!color) return undefined
  const alpha = Math.max(0, Math.min(1, (color.a ?? 1) * opacity))
  return `rgba(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)}, ${alpha})`
}

function paint(paints?: FigmaPaint[]): string | undefined {
  const value = paints?.find((item) => item.visible !== false && (item.opacity ?? 1) > 0)
  if (!value) return undefined
  if (value.type === "SOLID") return rgba(value.color, value.opacity ?? 1)
  if (value.type === "GRADIENT_LINEAR" && value.gradientStops?.length) {
    const stops = value.gradientStops
      .map((stop) => `${rgba(stop.color, value.opacity ?? 1)} ${stop.position * 100}%`)
      .join(", ")
    return `linear-gradient(90deg, ${stops})`
  }
  return undefined
}

function radius(node: FigmaNode): string | undefined {
  if (typeof node.cornerRadius === "number") return `${node.cornerRadius}px`
  if (
    typeof node.topLeftRadius === "number" ||
    typeof node.topRightRadius === "number" ||
    typeof node.bottomRightRadius === "number" ||
    typeof node.bottomLeftRadius === "number"
  ) {
    return [
      node.topLeftRadius ?? 0,
      node.topRightRadius ?? 0,
      node.bottomRightRadius ?? 0,
      node.bottomLeftRadius ?? 0,
    ].map((value) => `${value}px`).join(" ")
  }
  return undefined
}

function box(node: FigmaNode, local = false): CSSProperties {
  const style: CSSProperties = {
    position: "absolute",
    left: local ? 0 : (node.x ?? 0),
    top: local ? 0 : (node.y ?? 0),
    width: node.width ?? 0,
    height: node.height ?? 0,
    margin: 0,
    padding: 0,
    boxSizing: "border-box",
  }

  if (node.visible === false) style.display = "none"
  if (typeof node.opacity === "number") style.opacity = node.opacity

  const background = paint(node.fills)
  if (background) style.background = background

  const borderRadius = radius(node)
  if (borderRadius) style.borderRadius = borderRadius

  const stroke = paint(node.strokes)
  const strokeWeight = node.strokeWeight ?? 0
  if (stroke && strokeWeight > 0) {
    if (node.strokeAlign === "OUTSIDE") {
      style.boxShadow = `0 0 0 ${strokeWeight}px ${stroke}`
    } else if (node.strokeAlign === "CENTER") {
      style.boxShadow = `0 0 0 ${strokeWeight / 2}px ${stroke}`
    } else {
      style.boxShadow = `inset 0 0 0 ${strokeWeight}px ${stroke}`
    }
  }

  return style
}

function semanticBox(
  node: FigmaNode,
  options: {
    local?: boolean
    background?: string
    borderColor?: string
  } = {},
): CSSProperties {
  const style = box(node, options.local ?? false)

  if (options.background) style.background = options.background

  if (options.borderColor) {
    const strokeWeight = node.strokeWeight ?? 1
    if (node.strokeAlign === "OUTSIDE") {
      style.boxShadow = `0 0 0 ${strokeWeight}px ${options.borderColor}`
    } else if (node.strokeAlign === "CENTER") {
      style.boxShadow = `0 0 0 ${strokeWeight / 2}px ${options.borderColor}`
    } else {
      style.boxShadow = `inset 0 0 0 ${strokeWeight}px ${options.borderColor}`
    }
  } else if (node.strokes?.length) {
    style.boxShadow = "none"
  }

  return style
}

function semanticText(node: FigmaNode, color: string): CSSProperties {
  return { ...text(node), color }
}

function fontFamily(node: FigmaNode): string | undefined {
  const family = node.fontName?.family
  if (!family) return undefined
  if (family.toLowerCase().includes("sf pro")) {
    return '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", sans-serif'
  }
  return `"${family}", sans-serif`
}

function text(node: FigmaNode): CSSProperties {
  const style = box(node)
  style.background = "transparent"
  style.color = paint(node.fills)
  style.fontFamily = fontFamily(node)
  style.fontSize = node.fontSize
  style.fontWeight = node.fontWeight
  style.fontSynthesis = "none"
  style.whiteSpace = "pre"
  style.overflow = "visible"

  if (node.lineHeight?.unit === "PIXELS" && typeof node.lineHeight.value === "number") {
    style.lineHeight = `${node.lineHeight.value}px`
  } else {
    style.lineHeight = "normal"
  }

  if (typeof node.letterSpacing?.value === "number") {
    style.letterSpacing =
      node.letterSpacing.unit === "PERCENT"
        ? `${node.letterSpacing.value}%`
        : `${node.letterSpacing.value}px`
  }

  if (node.textAlignHorizontal === "CENTER") style.textAlign = "center"
  if (node.textAlignHorizontal === "RIGHT") style.textAlign = "right"
  if (node.textAlignHorizontal === "LEFT") style.textAlign = "left"

  return style
}

function node(nodes: ExactNodes, key: string): FigmaNode {
  const value = nodes[key]
  if (!value) throw new Error(`Missing generated Figma node: ${key}`)
  return value
}

export function SignalConfirmationSheet({
  platform,
  vehicle,
  selectedSignal,
  allowReply,
  confirmedReal,
  isSending,
  error,
  onAllowReplyChange,
  onConfirmedRealChange,
  onBack,
  onSend,
}: SignalConfirmationSheetProps) {
  const { t } = useI18n()
  const status: StatusVariant = vehicle.current_status ? "withFrame" : "noFrame"
  const disabled = exact.variants[variantKey(platform, status, "disabled")]
  const ready = exact.variants[variantKey(platform, status, "ready")]

  if (!disabled || !ready) {
    throw new Error(`Missing exact Signal Confirmation variant: ${platform}/${status}`)
  }

  const base = disabled.nodes
  const replyNodes = allowReply ? ready.nodes : disabled.nodes
  const consentNodes = confirmedReal ? ready.nodes : disabled.nodes
  const sendNodes = confirmedReal ? ready.nodes : disabled.nodes

  const bottomSheet = node(base, "bottomSheet")
  const grabber = node(base, "grabber")
  const plateHeading = node(base, "plateHeading")
  const plateContainer = node(base, "plateContainer")
  const signalHeading = node(base, "signalHeading")
  const selected = node(base, "selectedSignal")
  const signalBase = node(base, "signalBase")
  const signalLabel = node(base, "signalLabel")
  const signalIconCircle = node(base, "signalIconCircle")
  const signalIconSlot = node(base, "signalIconSlot")

  const reply = node(replyNodes, "reply")
  const replyTitle = node(replyNodes, "replyTitle")
  const replyToggle = node(replyNodes, "replyToggle")
  const replyThumb = node(replyNodes, "replyThumb")
  const replyBody = node(replyNodes, "replyBody")
  const replyTouch = node(replyNodes, "replyTouch")

  const consent = node(consentNodes, "consent")
  const consentText = node(consentNodes, "consentText")
  const consentCheckbox = node(consentNodes, "consentCheckbox")
  const consentTouch = node(consentNodes, "consentTouch")

  const warning = node(base, "warning")
  const warningText = node(base, "warningText")
  const back = node(base, "back")
  const backText = node(base, "backText")
  const send = node(sendNodes, "send")
  const sendText = node(sendNodes, "sendText")

  const signalColor = exactFigmaSignalColor(selectedSignal.category, selectedSignal.slug)
  const hasGlyph = hasExactFigmaSignalGlyph(selectedSignal.category, selectedSignal.slug)

  const rootStyle: CSSProperties = {
    position: "absolute",
    left: "calc(var(--bbf-rail-left, var(--bbf-safe-left, 0px)) + 16px)",
    right: "auto",
    bottom: 0,
    width: "calc(var(--bbf-rail-width, 100%) - 32px)",
    height: disabled.placement.height,
    margin: 0,
    padding: 0,
    overflow: "hidden",
    boxSizing: "border-box",
  }

  const signalCircleStyle = semanticBox(signalIconCircle, {
    background: "var(--bb-figma-surface-signal-choice)",
  })
  if (signalColor) {
    signalCircleStyle.boxShadow = `inset 0 0 0 ${signalIconCircle.strokeWeight ?? 1}px ${signalColor}`
  }

  const replyStyle = semanticBox(reply, {
    background: "var(--bb-figma-surface-control)",
    borderColor: "var(--bb-figma-action-primary)",
  })
  const replyToggleStyle = semanticBox(replyToggle, {
    background: allowReply
      ? "var(--bb-figma-action-primary)"
      : "var(--bb-figma-surface-control)",
    borderColor: allowReply ? undefined : "var(--bb-figma-border-default)",
  })
  const consentStyle = semanticBox(consent, {
    background: "var(--bb-figma-surface-control)",
  })
  const backStyle = semanticBox(back, {
    local: true,
    background: "var(--bb-figma-surface-control)",
  })
  const sendStyle = semanticBox(send, {
    local: true,
    background: confirmedReal
      ? "var(--bb-figma-action-primary)"
      : "var(--bb-figma-disabled-background)",
  })

  return (
    <section
      className="bbsc-sheet"
      style={rootStyle}
      role="dialog"
      aria-modal="true"
      aria-label={t("signalConfirmation.title")}
      data-figma-source={disabled.source.packagePath}
      data-figma-screen={`${disabled.screen.width}x${disabled.screen.height}`}
    >
      <div
        className="bbsc-bottom-sheet"
        style={{
          ...semanticBox(bottomSheet, {
            local: true,
            background: "var(--bb-figma-surface-card)",
          }),
          width: "100%",
          height: "100%",
        }}
      />
      <span
        className="bbsc-grabber"
        style={{
          ...semanticBox(grabber, { background: "var(--bb-figma-text-muted)" }),
          left: "50%",
          transform: "translateX(-50%)",
        }}
        aria-hidden="true"
      />

      <div className="bbsc-content-scroll">
        <p className="bbsc-heading" style={semanticText(plateHeading, "var(--bb-figma-text-section-header)")}>{t("signalConfirmation.plateHeading")}</p>

        <div className="bbsc-plate-flow" style={box(plateContainer)}>
          <FigmaStatusPlate vehicle={vehicle} />
        </div>

        <p className="bbsc-heading bbsc-signal-heading" style={semanticText(signalHeading, "var(--bb-figma-text-section-header)")}>{t("signalConfirmation.signalHeading")}</p>

        <div className="bbsc-selected-signal" style={box(selected)}>
          <div
            style={{
              ...semanticBox(signalBase, {
                local: true,
                background: "var(--bb-figma-surface-signal-choice)",
                borderColor: "var(--bb-figma-border-default)",
              }),
              width: "100%",
            }}
          />
          <span style={semanticText(signalLabel, "var(--bb-figma-text-primary)")}>{selectedSignal.title}</span>
          <span style={signalCircleStyle} aria-hidden="true" />
          <span className="bbsc-signal-glyph-slot" style={box(signalIconSlot)} aria-hidden="true">
            {hasGlyph && <FigmaSignalGlyph category={selectedSignal.category} slug={selectedSignal.slug} />}
          </span>
        </div>

        <button
          type="button"
          className="bbsc-card-button bbsc-reply-card"
          style={replyStyle}
          aria-pressed={allowReply}
          onClick={() => onAllowReplyChange(!allowReply)}
        >
          <span style={semanticText(replyTitle, "var(--bb-figma-text-primary)")}>{t("signalConfirmation.replyTitle")}</span>
          <span className="bbsc-toggle" style={replyToggleStyle} aria-hidden="true">
            <i style={semanticBox(replyThumb, { background: "var(--bb-figma-action-on-primary)" })} />
          </span>
          <span style={{ ...semanticText(replyBody, "var(--bb-figma-text-subtitle)"), width: "calc(100% - 40px)" }}>
            {t("signalConfirmation.replyBody")}
          </span>
          <i className="bbsc-touch-area" style={{ ...box(replyTouch), width: "100%" }} aria-hidden="true" />
        </button>

        <button
          type="button"
          className="bbsc-card-button bbsc-consent-card"
          style={consentStyle}
          aria-pressed={confirmedReal}
          onClick={() => onConfirmedRealChange(!confirmedReal)}
        >
          <span style={{ ...semanticText(consentText, "var(--bb-figma-text-primary)"), width: "calc(100% - 70px)" }}>
            {t("signalConfirmation.consentText")}
          </span>
          <img
            className="bbsc-checkbox"
            style={box(consentCheckbox)}
            src={confirmedReal ? checkboxOn : checkboxOff}
            alt=""
            draggable={false}
          />
          <i className="bbsc-touch-area" style={{ ...box(consentTouch), width: "100%" }} aria-hidden="true" />
        </button>

        <div className="bbsc-warning" style={box(warning)} role={error ? "alert" : undefined}>
          <span
            style={{
              ...semanticText(warningText, "var(--bb-figma-text-subtitle)"),
              ...(error ? { color: "var(--bb-figma-text-destructive)", whiteSpace: "pre-wrap" } : {}),
            }}
          >
            {error || t("signalConfirmation.warning")}
          </span>
        </div>
      </div>

      <footer className="bbsc-footer">
        <button
          type="button"
          className="bbsc-action-button"
          style={{ ...backStyle, position: "relative", width: "100%", height: 50 }}
          onClick={onBack}
        >
          <span className="bbsc-action-label" style={semanticText(backText, "var(--bb-figma-text-primary)")}>{t("common.back")}</span>
        </button>

        <button
          type="button"
          className="bbsc-action-button"
          style={{ ...sendStyle, position: "relative", width: "100%", height: 50 }}
          disabled={!confirmedReal || isSending}
          onClick={onSend}
        >
          <span
            className="bbsc-action-label"
            style={semanticText(
              sendText,
              confirmedReal
                ? "var(--bb-figma-action-on-primary)"
                : "var(--bb-figma-disabled-text)",
            )}
          >
            {isSending ? t("signalConfirmation.sending") : t("signalConfirmation.send")}
          </span>
        </button>
      </footer>
    </section>
  )

}
